import {IconButton, Toolbar, Button, AppBar, Grid} from '@mui/material';
import {useState, useRef, useImperativeHandle, forwardRef} from 'react';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import {useTranslation} from "react-i18next";
import {observer} from 'mobx-react';
import {stateIsValid, latIsValid, longIsValid, STEP_REG, STEP_LOC, STEP_SPEC, FIRST_OBS_STEP, NUM_OBS_STEPS, MAX_STEP} from './Util.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {SpeechButton} from './SpeechButton.js';
import {TaxNavi} from './taxons/Taxons.js';
import DayReport from './DayReport.js';
import {CalNavi} from './Calendar.js';
import {stateList} from './Lists.js';

const MainNavi = observer(() => {
	if (window.location.pathname !== '/')
		return null;

	const gStorage = useGlobalStorage();
	const n_obs = gStorage.main.n_obs;
	const pg_sz = gStorage.main.pg_sz;
	const loading = gStorage.main.loading;
	let pg = gStorage.main.curr_pg;

	const prevPage = () => {
		if (loading || pg <= 0)
			return 0;

		gStorage.main.setCurrPg(--pg);
	}

	const nextPage = () => {
		if (loading || pg >= n_obs / pg_sz - 1)
			return 0;

		gStorage.main.setCurrPg(++pg);
	}

	const realPg = () => {
		if (n_obs)
			return pg + 1;

		return 0;
	}

	return (
		<Grid container direction={'row'} spacing={0} style={{flexGrow: 1}} justifyContent='center'>
			<Grid>
				<Button variant="contained" color="primary" size="small" style={{width: 64, margin: 8}} onClick={prevPage} disabled={pg <= 0}>&lt;&lt;</Button>
				{realPg()} / {parseInt((n_obs + pg_sz - 1) / pg_sz, 10)}
				<Button variant="contained" color="primary" size="small" style={{width: 64, margin: 8}} onClick={nextPage} disabled={pg >= n_obs / pg_sz - 1}>&gt;&gt;</Button>
			</Grid>
		</Grid>
	);
});

const AddNavi = observer(forwardRef((props, ref) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function nValidSubObs() {
		let n = 0;

		for (let i = 0; i < NUM_OBS_STEPS; i++) 
			if (gStorage.add.obs.subobs[i].state_valid && gStorage.add.obs.subobs[i].num > 0)
				n++;
		return n;
	}

	function dataValidForSave() {
		if (gStorage.add.saveInProg)
			return false;

// 		console.log('dataValidForSave', gStorage.add.obs.user_id, gStorage.conn.user.id);

		if (gStorage.add.obs.user_id !== gStorage.conn.user.id && gStorage.conn.user.id !== 1)
			return false;

		if (gStorage.add.personID.length < 1)
			return false;

		let _obs = gStorage.add.obs;

// 		console.log('dataValidForSave', 3, _obs.city_id, _obs.addr.length, 
// 			gStorage.add.start_d_valid, gStorage.add.start_t_valid, 
// 	      		latIsValid(_obs.lat_obs / 1000000.0), longIsValid(_obs.long_obs / 1000000.0),  
// 			_obs.spec_id, nValidSubObs(), gStorage.add.tripID.length);

		return _obs.city_id > 0 && _obs.addr.length > 0 && 
			gStorage.add.start_d_valid && gStorage.add.start_t_valid &&
			latIsValid(_obs.lat_obs / 1000000.0) && longIsValid(_obs.long_obs / 1000000.0) &&
			_obs.spec_id > 0 && nValidSubObs() > 0 && gStorage.add.tripID.length <= 3;
	}

	function stepValidForNext() {
		if (gStorage.add.saveInProg)
			return 0;

		if (gStorage.add.currentStep === STEP_REG) {
			return gStorage.add.tripID.length <= 3 && gStorage.add.personID.length >= 1;
		} else if (gStorage.add.currentStep === STEP_LOC) {
			return gStorage.add.obs.city_id > 0 && gStorage.add.obs.addr.length > 0 && 
				gStorage.add.start_d_valid && gStorage.add.start_t_valid;
		} else if (gStorage.add.currentStep === STEP_SPEC) {
			return gStorage.add.obs.spec_id > 0;
		} else if (gStorage.add.currentStep < FIRST_OBS_STEP + NUM_OBS_STEPS) {
			let _so_id = gStorage.add.currentStep - FIRST_OBS_STEP;

			return gStorage.add.obs.subobs[_so_id].state_valid && gStorage.add.obs.subobs[_so_id].num > 0 && 
				(gStorage.add.obs.user_id === gStorage.conn.user.id || gStorage.conn.user.id === 1);
		}

		return false;
	}

	function stepValidForPrev() {
		if (gStorage.add.saveInProg)
			return 0;

		return gStorage.add.currentStep > 0;
	}

	function nextStep() {
		let step = gStorage.add.currentStep;

		if (step < MAX_STEP) 
			step = step + 1;

		if (step >= FIRST_OBS_STEP) {
			let so = step - FIRST_OBS_STEP;

			gStorage.add.setSOValid(so, 
				stateIsValid(gStorage.add.obs.subobs[so].sum_desc, stateList));
		} 

		gStorage.add.setStep(step);
	}
  
	function prevStep() {
		let step = gStorage.add.currentStep;

		if (step > 0)
			step = step - 1;

		if (step >= FIRST_OBS_STEP) {
			let so = step - FIRST_OBS_STEP;

			gStorage.add.setSOValid(so, 
				stateIsValid(gStorage.add.obs.subobs[so].sum_desc, stateList));
		}

		gStorage.add.setStep(step);
	}

	useImperativeHandle(ref, () => ({
		dataValidForSave,
		stepValidForNext,
		stepValidForPrev,
		nextStep,
		prevStep
	}));

	if (window.location.pathname !== '/add')
		return null;

	return (
		<Grid container direction={'row'} spacing={0} style={{flexGrow: 1}} justifyContent='center'>
			<Grid>
				<Button variant="contained" color="primary" size="small" style={{width:64, margin: 8}} onClick={prevStep} disabled={!stepValidForPrev()}>&lt;&lt;</Button>
				<Button variant="contained" color="primary" size="small" style={{width:64, margin: 8}} onClick={props.saveObs} disabled={!dataValidForSave()}>{t('add.save')}</Button>
				<Button variant="contained" color="primary" size="small" style={{width:64, margin: 8}} onClick={nextStep} disabled={!stepValidForNext()}>&gt;&gt;</Button>
			</Grid>
		</Grid>
	);
}))

const DayReportButton = observer((props) => {
	if (!props.visible)
		return null;
	
	return (
		<IconButton sx={{color: 'white'}} size='small' onClick={props.openReport}>
			<ViewDayIcon/>
		</IconButton>
	);
})

const DummyButton = observer((props) => {
	if (!props.visible)
		return null;
	
	return (
		<IconButton sx={{color: '#1976d2'}} size='small'>
			<ViewDayIcon/>
		</IconButton>
	);
})

const BottomBar = observer((props) => {
	const [dayReportIsOpen, setDayReportIsOpen] = useState(false);
	const gStorage = useGlobalStorage();
	const addRef = useRef();

	let bg = gStorage.conn.is_auth && gStorage.conn.user.id === 1 ? 'secondary' : 'primary';

	function toggleDayReport() {
		if (!dayReportIsOpen) {
			if (window.location.pathname !== '/') {
				return;
			}
		} else {
			/* Unfocus clicked button to prevent aria-hidden warnings. */
			document.activeElement.blur();
		}

		gStorage.dayrep.setStats(null);
		setDayReportIsOpen(!dayReportIsOpen);
	}

	function bbRecAction(cmd, rec) {
		console.log('bbRecAction', cmd);

		if (window.location.pathname === '/add') {
			if (cmd === 'next') {
				if (addRef.current?.stepValidForNext()) {
					addRef.current.nextStep();
					return;
				}
			} else if (cmd === 'previous') {
				if (addRef.current?.stepValidForPrev()) {
					addRef.current.prevStep();
					return;
				}
			} else if (cmd === 'save') {
				if (addRef.current?.dataValidForSave()) {
					props.saveObs();
					return;
				}
			}
		}

		// console.log('higher', rec.higherAction ? true : false);

		if (rec.higherAction)
			rec.higherAction(cmd);

	}

	const rec={
		higherAction: props.rec ? props.rec.action : null,
		action: bbRecAction,
		continuous: props.rec ? props.rec.continuous : false,
	};

	const dayRepVisible = gStorage.conn.online && gStorage.conn.connected && window.location.pathname === '/';
	const speechVisible = gStorage.conn.user.speech_rec && gStorage.speechRec.recognizer && 
		(window.location.pathname === '/' || window.location.pathname === '/add' || window.location.pathname === '/taxons');

	/* 
	 * NOTE: ylimääräinen Toolbar AppBar:n jälkeen on tarpeellinen. 
	 * Jos sen poistaa, alareunan rivejä jää piiloon! 
	 */
	return (
		<div style={{flexGrow: 1}} id='bottombar'>
			<AppBar position="fixed" color={bg} sx={{top: 'auto', bottom: 1}}>
				<Toolbar variant="dense">
					{dayRepVisible ? 
						<DayReportButton visible={dayRepVisible} openReport={toggleDayReport} /> :
						<DummyButton visible={!dayRepVisible} />
					}
					<MainNavi />
					<AddNavi ref={addRef} { ...props} />
					<TaxNavi {...props} />
					<CalNavi {...props} />
					{speechVisible ?
						<SpeechButton visible={speechVisible} rec={rec} /> :
						<DummyButton visible={!speechVisible} />
					}
				</Toolbar>
			</AppBar>
			<Toolbar variant="dense" />

			<DayReport 
			    isOpen={dayReportIsOpen}
			    close={toggleDayReport}
			/>
		</div>
	);
});

export default BottomBar;
